import AddableTable from '@gp/components/AddableTable';
import ConfirmableBtn from '@gp/components/ConfirmableBtn';
import { getHasGRMilestoneFinancialsData } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { getRecordKeyOfMockable } from '@gp/utils/common';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { Button, Table, TableAddon, TableColumn, TableProps } from 'tea-component';
import {
  buildRecord,
  getColumns,
  getIsKeyMilestones,
  getKeyMilestoneFinancialPeriodFlagInfo,
} from './utils';

import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { IMilestoneFinancialVO } from '@gp/types/milestones';
import type { IBaseProps } from '@home/types/common';

import './Table.scss';

interface IKeyMilestonesFinancialsTableProps extends IBaseProps, Omit<TableProps, 'columns'> {
  isEdit?: boolean;
  currentMilestoneDate?: string;
  milestones?: IMilestone[];
  value?: IMilestoneFinancialVO[];
  onChange?: (payload: IMilestoneFinancialVO[]) => void;
}

const { autotip } = Table.addons;

const KeyMilestonesFinancialsTable: React.FC<IKeyMilestonesFinancialsTableProps> = (props) => {
  const {
    style,
    className,
    isEdit = false,
    currentMilestoneDate,
    milestones,
    value: records,
    onChange: updateRecords,
    ...restProps
  } = props;

  const addons = useMemo<TableAddon<IMilestoneFinancialVO>[]>(
    () => [
      autotip({
        emptyText: (
          <div className="dbg-key-milestones-financials-table__no-data-wrapper">
            Financial data has not been updated at the current milestone.
          </div>
        ),
      }),
    ],
    [],
  );

  const reviewRecords = useMemo<IMilestoneFinancialVO[]>(() => {
    const hasGRMilestoneFinancialsData = getHasGRMilestoneFinancialsData(records);
    return hasGRMilestoneFinancialsData ? (records as IMilestoneFinancialVO[]) : [];
  }, [records]);

  const columns = useMemo<TableColumn<IMilestoneFinancialVO>[]>(() => {
    const milestonesNames = records?.map((record) => record?.milestone) || [];
    const milestonesFiltered = milestones?.filter((milestone) => {
      const { milestone: milestoneName } = milestone || {};
      return !milestonesNames?.includes(milestoneName);
    });
    const contextColumns = getColumns({
      isEdit,
      milestones: milestonesFiltered,
      onChange: (cvm, key, value) => {
        const { mock_id: mockId } = cvm || {};
        const recordIdx = records!.findIndex((record) => record?.mock_id === mockId);
        const record = records![recordIdx];
        const recordNew = { ...record, [key]: value };
        const recordsNew = [
          ...records!.slice(0, recordIdx),
          recordNew,
          ...records!.slice(recordIdx + 1),
        ];
        updateRecords?.(recordsNew);
      },
      onMilestoneChange: (cvm, value) => {
        const { mock_id: mockId } = cvm || {};
        const milestoneNew = milestonesFiltered!.find(
          (milestone) => milestone?.milestone_id === value,
        );
        const {
          parent_milestone_id: parentMilestoneIdNew,
          milestone: milestoneNameNew,
          date: milestoneDateNew,
        } = milestoneNew || {};
        const milestonePeriodFlagInfo = getKeyMilestoneFinancialPeriodFlagInfo(
          milestoneDateNew,
          currentMilestoneDate,
        );
        const recordIdx = records!.findIndex((record) => record?.mock_id === mockId);
        const record = records![recordIdx];
        const recordNew = {
          ...record,
          ...milestonePeriodFlagInfo,
          milestone_id: value,
          parent_milestone_id: parentMilestoneIdNew || '',
          milestone: milestoneNameNew || '',
          start_date: milestoneDateNew || '',
          dev_cost: '',
          dev_hc: '',
        };
        const recordsNew = [
          ...records!.slice(0, recordIdx),
          recordNew,
          ...records!.slice(recordIdx + 1),
        ];
        updateRecords?.(recordsNew);
      },
    });

    if (!isEdit) return contextColumns;
    return [
      ...contextColumns,
      {
        key: 'action',
        header: 'Actions',
        width: 100,
        align: 'left',
        render: (cvm) => {
          const { milestone: milestoneName } = cvm || {};
          if (getIsKeyMilestones(milestoneName)) return '-';
          return (
            <ConfirmableBtn
              // 二次确认弹窗的标题
              confirmTitle="Do you want to delete key milestone?"
              // 二次确认弹窗的描述
              confirmDescription="The deleted information will not be recovered."
              // @ts-ignore
              confirmModalCfg={{ size: 520 }}
              // 二次确认弹窗点击confirm的回调
              onConfirm={() => {
                const { mock_id: mockId } = cvm || {};
                const recordIdx = records!.findIndex((record) => record?.mock_id === mockId);
                const recordsNew = [
                  ...records!.slice(0, recordIdx),
                  ...records!.slice(recordIdx + 1),
                ];
                updateRecords?.(recordsNew);
              }}
              maskStyle={{
                zIndex: 1001,
              }}
            >
              <Button type="link" disabled={false}>
                Delete
              </Button>
            </ConfirmableBtn>
          );
        },
      },
    ];
  }, [isEdit, currentMilestoneDate, milestones, records, updateRecords]);

  return (
    <>
      {isEdit || (
        <div className={classnames('dbg-key-milestones-financials-table is-review', className)}>
          <Table
            {...restProps}
            disableHoverHighlight
            style={restProps?.tableStyle}
            className={restProps?.tableClassName}
            recordKey={getRecordKeyOfMockable}
            records={reviewRecords}
            addons={addons}
            columns={columns}
          />
        </div>
      )}
      {isEdit && (
        <AddableTable
          {...restProps}
          style={style}
          className={classnames('dbg-key-milestones-financials-table is-edit', className)}
          recordKey={getRecordKeyOfMockable}
          records={records}
          columns={columns}
          onAdd={() => {
            const recordsNew = [...(records || []), buildRecord()];
            updateRecords?.(recordsNew);
          }}
        />
      )}
    </>
  );
};

export default KeyMilestonesFinancialsTable;
